import { CanvasTexture, SRGBColorSpace, RepeatWrapping } from 'three';
import type { DisplayConfig } from '../../types';

/**
 * 차량 실내 디스플레이에 띄우는 쇼룸 로고 화면.
 *
 * 왜 로고 이미지를 그대로 쓰지 않고 캔버스에 다시 그리는가:
 *   로고는 1000x1000 정사각인데 차량 디스플레이는 16:10 정도의 가로 화면입니다.
 *   이미지를 UV에 그대로 얹으면 좌우로 늘어나 찌그러집니다. 그래서 화면 비율에 맞는
 *   캔버스를 만들고, 그 안에 로고를 비율 유지로 앉힙니다. 남는 자리는 배경색으로
 *   채워서 "켜져 있는 화면"처럼 보이게 합니다.
 */

export { SHOWROOM_LOGO_URL } from '../brandAssets';

// 캔버스 가로 해상도. 실제 화면이 0.34m라 이 정도면 충분히 선명합니다.
const CANVAS_WIDTH = 1024;
// 로고가 화면에서 차지할 최대 비율. 여백이 있어야 화면처럼 보입니다.
const LOGO_MAX_FILL = 0.62;

export interface LogoPlacementInput {
  canvasWidth: number;
  canvasHeight: number;
  logoWidth: number;
  logoHeight: number;
  maxFill?: number;
}

/** 캔버스 좌표계 배치. aspect/sourceAspect는 비율이 지켜졌는지 확인용입니다. */
export interface LogoPlacement {
  x: number;
  y: number;
  width: number;
  height: number;
  aspect: number;
  sourceAspect: number;
}

/**
 * 로고를 화면 안에 비율 유지로 앉힐 위치와 크기를 구합니다.
 *
 * 캔버스 그리기와 분리한 이유는 이 계산이 틀리면 로고가 찌그러지거나 잘리는데,
 * 그건 그림을 렌더해 봐야만 알 수 있는 종류의 버그가 아니라 숫자로 검증할 수 있는
 * 종류이기 때문입니다.
 */
export function computeLogoPlacement({
  canvasWidth,
  canvasHeight,
  logoWidth,
  logoHeight,
  maxFill = LOGO_MAX_FILL,
}: LogoPlacementInput): LogoPlacement {
  const logoAspect = logoWidth / logoHeight;
  // 가로·세로 중 먼저 한계에 닿는 쪽에 맞춥니다(contain).
  const boxWidth = canvasWidth * maxFill;
  const boxHeight = canvasHeight * maxFill;
  const scale = Math.min(boxWidth / logoWidth, boxHeight / logoHeight);
  const width = logoWidth * scale;
  const height = logoHeight * scale;
  return {
    x: (canvasWidth - width) / 2,
    y: (canvasHeight - height) / 2,
    width,
    height,
    // 비율이 유지됐는지 호출자가 확인할 수 있게 함께 돌려줍니다.
    aspect: width / height,
    sourceAspect: logoAspect,
  };
}

/** 캔버스에 그릴 수 있고 원본 크기를 아는 이미지. */
type LogoImage = CanvasImageSource & { width: number; height: number };

/**
 * drei useTexture가 돌려준 로고.
 *
 * three의 Texture.image는 any로 선언돼 있어서, 이 함수가 실제로 읽는 것만
 * (원본 크기와 그릴 대상) 좁혀서 받습니다. 아직 로드되지 않았으면 비어 있습니다.
 */
export interface LogoTextureSource {
  image?: unknown;
}

/**
 * 로고 텍스처를 화면 비율에 맞춘 CanvasTexture로 다시 굽습니다.
 *
 * @param logoTexture drei useTexture로 불러온 로고
 * @param display 모델별 화면 설정
 * @returns 캔버스를 못 쓰는 환경에서는 null
 */
export function createDisplayTexture(
  logoTexture: LogoTextureSource | null | undefined,
  display: DisplayConfig
): CanvasTexture | null {
  /*
   * 런타임 값은 GLTF/텍스처 로더가 만든 HTMLImageElement입니다. three가 any로
   * 두고 있어서 여기서 한 번 좁혀 주고, 이후로는 크기와 그리기만 씁니다.
   */
  const image = logoTexture?.image as LogoImage | null | undefined;
  if (!image) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_WIDTH;
  canvas.height = Math.round(CANVAS_WIDTH / display.aspect);
  const context = canvas.getContext('2d');
  if (context === null) {
    // jsdom처럼 2D 컨텍스트가 없는 환경입니다. 화면은 원본 머티리얼로 남습니다.
    return null;
  }

  context.fillStyle = display.backgroundColor;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const placement = computeLogoPlacement({
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
    logoWidth: image.width,
    logoHeight: image.height,
  });
  context.drawImage(image, placement.x, placement.y, placement.width, placement.height);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  /*
   * flipY는 true여야 합니다(CanvasTexture 기본값).
   *
   * glTF 텍스처는 보통 flipY = false로 쓰지만, 이 화면 메시의 UV는 v가 위에서
   * 아래로 흐르지 않고 아래에서 위로 흐릅니다. false로 두면 로고가 180도 돌아서
   * 글자를 읽을 수 없습니다. 렌더해서 원본 로고와 비교해 확인했습니다
   * (원본은 핀 아이콘이 위 / "towncar" 글자가 아래).
   *
   * 기본값에 의존하지 않고 명시하는 이유는, 이 값이 우연이 아니라 확인해서 고른
   * 값이라는 걸 남겨야 다음에 건드릴 때 되돌리지 않기 때문입니다.
   */
  texture.flipY = true;
  /*
   * Model 3의 화면 UV는 v가 0~2입니다(앞 화면과 뒷좌석 화면이 같은 텍스처를
   * 위아래로 나눠 씁니다). 반복 래핑이 아니면 v > 1 구간이 가장자리 픽셀로
   * 늘어나 뒷좌석 화면이 한 줄 색으로 채워집니다.
   */
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  return texture;
}
